import { View, Text } from 'react-native'
import { Image } from '@/components/ui/Image'
import { Pressable } from '@/components/ui/Pressable'
import { useTheme } from '@/lib/hooks/use-theme'
import { useSemanticColors } from '@/lib/theme/tokens'
import { FONTS, SPACING, RADIUS } from '@/lib/brand'
import { GENERIC_IMAGE_BLURHASH } from '@/lib/utils/blurhash'

/** Landscape cover — marketplace checkout, not a letterboxed thumbnail. */
export const CHECKOUT_COVER_ASPECT = 16 / 10

export function CheckoutVehicleHero({
  title,
  dateLabel,
  imageUrl,
  blurhash,
  eyebrow,
  onPhotoPress,
}: {
  title: string
  dateLabel?: string | null
  imageUrl?: string | null
  blurhash?: string | null
  eyebrow?: string
  onPhotoPress?: () => void
}) {
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)

  return (
    <View testID="checkout.vehicle-hero">
      <View
        style={{
          width: '100%',
          aspectRatio: CHECKOUT_COVER_ASPECT,
          borderRadius: RADIUS.xl,
          overflow: 'hidden',
          backgroundColor: colors.surfaceSubtle,
        }}
      >
        {imageUrl ? (
          <Pressable
            onPress={onPhotoPress}
            disabled={!onPhotoPress}
            accessibilityRole="button"
            accessibilityLabel={title}
            testID="checkout.vehicle-photo"
            style={{
              position: 'absolute',
              top: 0,
              right: 0,
              bottom: 0,
              left: 0,
            }}
          >
            <Image
              source={imageUrl}
              // Tours and older bookings may not have a per-image hash yet. A
              // shared placeholder keeps the hero intentional during the fetch
              // instead of flashing a flat grey rectangle.
              blurhash={blurhash ?? GENERIC_IMAGE_BLURHASH}
              width="100%"
              height="100%"
              contentFit="cover"
              accessibilityLabel={title}
            />
          </Pressable>
        ) : null}
      </View>
      <View style={{ marginTop: SPACING.md, gap: SPACING.xs }}>
        {eyebrow ? (
          <Text
            style={{
              fontFamily: FONTS.body,
              fontSize: 13,
              color: colors.textSecondary,
            }}
          >
            {eyebrow}
          </Text>
        ) : null}
        <Text
          testID="checkout.vehicle-title"
          style={{
            fontFamily: FONTS.bodyBold,
            fontSize: 22,
            lineHeight: 28,
            color: colors.textPrimary,
          }}
        >
          {title}
        </Text>
        {dateLabel ? (
          <Text
            testID="checkout.trip-dates"
            style={{
              fontFamily: FONTS.body,
              fontSize: 15,
              lineHeight: 21,
              color: colors.textSecondary,
            }}
          >
            {dateLabel}
          </Text>
        ) : null}
      </View>
    </View>
  )
}
